// Example usage:
//
// import ProductRibbon from './ProductRibbon';
//
// function App() {
//   return (
//     <div style={{ position: 'relative', height: '300px', background: '#eee' }}>
//       <ProductRibbon
//         type="ribbon1"
//         text="SALE 10%"
//         color="red"
//         size="md"
//         position={{ right: 'right-5' }}
//       />
//     </div>
//   );
// }
import React, { JSX } from 'react';

type ProductRibbonProps = {
  label: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'sale';
};

const Ribbon2 = ({ label }: { label: string }) => {
  return (
    <div className="absolute -top-1 -right-1 z-10">
      <div className="relative">
        {/* Corner ribbon with fold effect */}
        <div className="relative">
          <div className="bg-linear-to-br from-red-600 to-red-700 text-white px-4 py-2 shadow-xl transform rotate-12 translate-x-2 -translate-y-2">
            <div className="flex items-center gap-1.5">
              <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-black text-sm tracking-wide uppercase drop-shadow-md">{label}</span>
              <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </div>
          {/* Fold shadow effect */}
          <div className="absolute -bottom-1 right-0 w-0 h-0 border-t-8 border-t-red-900 border-r-8 border-r-transparent"></div>
        </div>
      </div>
    </div>
  );
};

const ProductRibbon: React.FC<ProductRibbonProps> = ({ label, size = 'md', position = 'top-right', variant = 'default' }) => {
  // Special rendering for sale variant
  // if (variant === 'sale') return <Ribbon2 label={label} />;

  const typoClasses = {
    xs: 'text-xs ',
    sm: 'text-sm ',
    md: 'text-md ',
    lg: 'text-lg ',
  };
  const sizesClasses = {
    xs: 'h-6 px-2',
    sm: 'h-6 px-5',
    md: 'h-8 px-5',
    lg: 'h-12 px-14',
  };

  const positionClasses = {
    'top-left': 'top-0 left-0 ',
    'top-right': 'top-0 -right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
  };

  const variantClasses = {
    default: 'bg-primary-100 text-white',
    primary: 'bg-primary-200 text-white',
    secondary: 'bg-secondary text-white',
    success: 'bg-success text-white',
    warning: 'bg-warning text-white',
    error: 'bg-error text-white',
  };

  return (
    <div
      className={`
            product-ribbon animate-pulse- duration-10000 ease-in-out
            absolute flex items-center justify-center p-0.5
            ${positionClasses[position]} ${variantClasses[variant as keyof typeof variantClasses]}   `}
    >
      <div>
        <div
          className={`
                product-ribbon-wrap flex items-center justify-center
                ${sizesClasses[size]} border- border-white- border-dashed- shadow-lg
                `}
        >
          <span className={` ${typoClasses[size]} font-bold`}>{label}</span>
        </div>
      </div>
    </div>
  );
};

export default ProductRibbon;
